import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Calendar, CheckCircle2, Clock, Hash, Info, PencilLine, X } from 'lucide-react';
import type { ImportProgressData } from './ImportProgressModal';

interface Props {
  result: ImportProgressData | null;
  onApplyDuplicateResolutions?: (resolutions: {
    shots: Array<{ id: string; newCode: string }>;
    assets: Array<{ id: string; newCode: string }>;
  }) => void;
  onClose: () => void;
}

function formatCurrency(value: number | null | undefined) {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDays(value: number | null | undefined) {
  if (value === null || value === undefined) return 'N/A';
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function getAccuracyTone(accuracy: number | null | undefined) {
  if (accuracy === null || accuracy === undefined) return 'bg-slate-50 border-slate-100 text-slate-500';
  if (accuracy >= 98) return 'bg-emerald-50 border-emerald-100 text-emerald-700';
  if (accuracy >= 90) return 'bg-amber-50 border-amber-100 text-amber-700';
  return 'bg-rose-50 border-rose-100 text-rose-700';
}

function varianceTone(variance: number | null | undefined) {
  if (variance === null || variance === undefined || Math.abs(variance) < 0.01) {
    return 'text-emerald-600';
  }
  return variance > 0 ? 'text-amber-600' : 'text-rose-600';
}

export function ImportResultReport({ result, onApplyDuplicateResolutions, onClose }: Props) {
  if (!result || !result.isOpen || !result.isComplete) return null;

  const [shotRenames, setShotRenames] = useState<Record<string, string>>({});
  const [assetRenames, setAssetRenames] = useState<Record<string, string>>({});

  useEffect(() => {
    const nextShotRenames = Object.fromEntries((result.duplicateConflicts?.shots || []).map((candidate) => [candidate.id, candidate.suggestedCode]));
    const nextAssetRenames = Object.fromEntries((result.duplicateConflicts?.assets || []).map((candidate) => [candidate.id, candidate.suggestedCode]));
    setShotRenames(nextShotRenames);
    setAssetRenames(nextAssetRenames);
  }, [result]);

  const metrics = result.priceAccuracy
    ? [
        { label: 'Overall', metric: result.priceAccuracy.overall },
        { label: 'Shots', metric: result.priceAccuracy.shots },
        { label: 'Assets', metric: result.priceAccuracy.assets },
        { label: 'Onset', metric: result.priceAccuracy.onset },
      ]
    : [];

  const mismatchedMetrics = metrics.filter(({ metric }) => metric?.accuracy !== null && metric?.accuracy !== undefined && metric.accuracy < 99.9);
  const overallVariance =
    result.priceAccuracy?.overall?.imported !== null &&
    result.priceAccuracy?.overall?.imported !== undefined &&
    result.priceAccuracy?.overall?.calculated !== null &&
    result.priceAccuracy?.overall?.calculated !== undefined
      ? result.priceAccuracy.overall.calculated - result.priceAccuracy.overall.imported
      : null;
  const overallAccuracy = result.priceAccuracy?.overall?.accuracy;
  const hasAccuracySummary = Boolean(result.priceAccuracy && metrics.length > 0);
  const episodeRows = result.comparison?.episodes || [];
  const shotRows = result.comparison?.shots || [];
  const assetRows = result.comparison?.assets?.rows || [];
  const onsetComparison = result.comparison?.onset;
  const duplicateShots = result.duplicateConflicts?.shots || [];
  const duplicateAssets = result.duplicateConflicts?.assets || [];
  const duplicateShotCount = duplicateShots.length;
  const duplicateAssetCount = duplicateAssets.length;
  const diagnostics = result.diagnostics;
  const duplicateImportTotal = useMemo(
    () =>
      duplicateShots.reduce((sum, candidate) => sum + (candidate.imported || 0), 0) +
      duplicateAssets.reduce((sum, candidate) => sum + (candidate.imported || 0), 0),
    [duplicateAssets, duplicateShots]
  );
  const hasDuplicateConflicts = duplicateShots.length > 0 || duplicateAssets.length > 0;
  const outcomeSummary = result.outcomeSummary;

  const outcomeTone = (status: 'done' | 'warning' | 'info') => {
    if (status === 'done') return 'border-emerald-200 bg-emerald-50 text-emerald-800';
    if (status === 'warning') return 'border-amber-200 bg-amber-50 text-amber-800';
    return 'border-sky-200 bg-sky-50 text-sky-800';
  };

  const outcomeIcon = (status: 'done' | 'warning' | 'info') => {
    if (status === 'done') return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
    if (status === 'warning') return <AlertTriangle className="h-4 w-4 text-amber-600" />;
    return <Info className="h-4 w-4 text-sky-600" />;
  };

  const handleApplyDuplicates = () => {
    if (!onApplyDuplicateResolutions) return;
    onApplyDuplicateResolutions({
      shots: duplicateShots.map((candidate) => ({ id: candidate.id, newCode: shotRenames[candidate.id] || candidate.suggestedCode })),
      assets: duplicateAssets.map((candidate) => ({ id: candidate.id, newCode: assetRenames[candidate.id] || candidate.suggestedCode })),
    });
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 16 }}
          className="bg-white dark:bg-slate-950 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/70 dark:bg-slate-900/70">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-300">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Import Comparison Report</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {mismatchedMetrics.length > 0
                    ? 'The import finished, but some legacy quote values do not match the app calculation.'
                    : 'The import finished and the legacy quote lines up with the app calculation.'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-200"
              aria-label="Dismiss import report"
              title="Dismiss"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6 space-y-6 overflow-y-auto">
            {hasAccuracySummary && (
              <div className={`rounded-xl border p-4 ${getAccuracyTone(overallAccuracy)}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.18em]">Import Accuracy</div>
                    <div className="mt-1 text-2xl font-bold">
                      {overallAccuracy === null || overallAccuracy === undefined ? 'N/A' : `${overallAccuracy}%`}
                    </div>
                    <div className="mt-2 text-xs leading-5">
                      <div>Imported legacy total: {formatCurrency(result.priceAccuracy?.overall?.imported)}</div>
                      <div>Calculated app total: {formatCurrency(result.priceAccuracy?.overall?.calculated)}</div>
                    </div>
                  </div>
                  {mismatchedMetrics.length > 0 && (
                    <div className="max-w-sm text-xs leading-5">
                      {overallVariance === null
                        ? `${mismatchedMetrics.map((item) => item.label).join(', ')} differ from the imported legacy quote.`
                        : `Variance: ${formatCurrency(Math.abs(overallVariance))} ${overallVariance > 0 ? 'higher' : 'lower'} in the app.`}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-3 border border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-2 text-xs font-medium text-slate-500 mb-1">
                  <Clock className="w-3.5 h-3.5" />
                  Onset Days
                </div>
                <div className="text-lg font-bold text-slate-900 dark:text-slate-100">{formatDays(result.onsetDays)}</div>
              </div>
              <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-3 border border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-2 text-xs font-medium text-slate-500 mb-1">
                  <Calendar className="w-3.5 h-3.5" />
                  Bid Date
                </div>
                <div className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate">{result.bidDate || 'Not detected'}</div>
              </div>
              <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-3 border border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-2 text-xs font-medium text-slate-500 mb-1">
                  <Hash className="w-3.5 h-3.5" />
                  Version
                </div>
                <div className="text-sm font-bold text-slate-900 dark:text-slate-100">{result.version || 'Not detected'}</div>
              </div>
            </div>

            {outcomeSummary && (
              <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
                <div className="flex items-start gap-3">
                  <Info className="mt-0.5 h-5 w-5 shrink-0 text-slate-500" />
                  <div className="w-full space-y-4">
                    <div>
                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">Import outcome</div>
                      <div className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">
                        {outcomeSummary.mode === 'merge' ? 'Merge mode was used.' : 'Import mode was used.'}
                        {' '}This summary is the practical trail of what happened in the run.
                      </div>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
                      <div className="font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Source tabs</div>
                      <div className="mt-2">{outcomeSummary.sourceDocs.length > 0 ? outcomeSummary.sourceDocs.join(' • ') : 'No source tabs recorded.'}</div>
                    </div>
                    <div className="space-y-3">
                      {outcomeSummary.steps.map((step) => (
                        <div key={step.label} className={`rounded-lg border p-3 ${outcomeTone(step.status)}`}>
                          <div className="flex items-start gap-2">
                            <div className="mt-0.5 shrink-0">{outcomeIcon(step.status)}</div>
                            <div>
                              <div className="text-sm font-semibold">{step.label}</div>
                              <div className="mt-1 text-xs leading-5">{step.detail}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {diagnostics && (
              <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 text-sky-950 dark:border-sky-900/50 dark:bg-sky-950/20 dark:text-sky-100">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-sky-600" />
                  <div className="space-y-3 w-full">
                    <div className="space-y-1">
                      <div className="text-sm font-semibold">Import diagnostics</div>
                      <div className="text-xs leading-5 text-sky-800 dark:text-sky-200">
                        This is the parser/import count trail for the grouped quote import. It helps us see whether rows were never parsed, filtered out, or held back as duplicates.
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div className="rounded-lg border border-sky-200 bg-white/70 p-3 dark:border-sky-900/40 dark:bg-slate-950/30">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-700 dark:text-sky-300">Shots</div>
                        <div className="mt-2 space-y-1 text-sm text-slate-700 dark:text-slate-200">
                          <div>Parsed rows: <span className="font-semibold">{diagnostics.totalParsedShotRows}</span></div>
                          <div>Valid rows: <span className="font-semibold">{diagnostics.totalValidShotRows}</span></div>
                          <div>Imported: <span className="font-semibold">{diagnostics.totalImportedShots}</span></div>
                          <div>Duplicates held back: <span className="font-semibold">{diagnostics.totalDuplicateShots}</span></div>
                        </div>
                      </div>
                      <div className="rounded-lg border border-sky-200 bg-white/70 p-3 dark:border-sky-900/40 dark:bg-slate-950/30">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-700 dark:text-sky-300">Assets</div>
                        <div className="mt-2 space-y-1 text-sm text-slate-700 dark:text-slate-200">
                          <div>Parsed rows: <span className="font-semibold">{diagnostics.totalParsedAssetRows}</span></div>
                          <div>Valid rows: <span className="font-semibold">{diagnostics.totalValidAssetRows}</span></div>
                          <div>Imported: <span className="font-semibold">{diagnostics.totalImportedAssets}</span></div>
                          <div>Duplicates held back: <span className="font-semibold">{diagnostics.totalDuplicateAssets}</span></div>
                        </div>
                      </div>
                    </div>
                    {diagnostics.docs.length > 0 && (
                      <div className="rounded-lg border border-sky-200 bg-white/70 p-3 dark:border-sky-900/40 dark:bg-slate-950/30">
                        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-700 dark:text-sky-300">By Sheet</div>
                        <div className="space-y-2 text-xs text-slate-700 dark:text-slate-200">
                          {diagnostics.docs.map((doc) => (
                            <div key={`${doc.type}:${doc.docId}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-sky-100 pt-2 first:border-t-0 first:pt-0 dark:border-sky-900/30">
                              <span className="font-semibold text-slate-900 dark:text-slate-100">{doc.docName}</span>
                              <span className="uppercase tracking-[0.12em] text-sky-700 dark:text-sky-300">{doc.type}</span>
                              <span>Parsed {doc.parsedRows}</span>
                              <span>Valid {doc.validRows}</span>
                              <span>Imported {doc.importedRows}</span>
                              <span>Duplicates {doc.duplicateRows}</span>
                              {typeof doc.thumbnailMatches === 'number' && doc.thumbnailMatches > 0 ? (
                                <span>Thumbnails {doc.thumbnailMatches}</span>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {mismatchedMetrics.length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/20 p-4 text-amber-900 dark:text-amber-100">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                  <div className="space-y-1">
                    <div className="text-sm font-semibold">Legacy quote differences found</div>
                    <div className="text-xs leading-5 text-amber-800 dark:text-amber-200">
                      This report compares imported legacy costs against the app calculation by onset, episode, assets, and shots where needed.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {hasDuplicateConflicts && (
              <div className="rounded-xl border border-indigo-200 bg-indigo-50 dark:border-indigo-900/50 dark:bg-indigo-950/20 p-4 text-indigo-900 dark:text-indigo-100">
                <div className="flex items-start gap-3">
                  <PencilLine className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" />
                  <div className="space-y-1">
                    <div className="text-sm font-semibold">Duplicate codes were held back from import</div>
                    <div className="text-xs leading-5 text-indigo-800 dark:text-indigo-200">
                      {duplicateShotCount > 0 ? `${duplicateShotCount} shot${duplicateShotCount === 1 ? "" : "s"}` : null}
                      {duplicateShotCount > 0 && duplicateAssetCount > 0 ? " and " : null}
                      {duplicateAssetCount > 0 ? `${duplicateAssetCount} asset${duplicateAssetCount === 1 ? "" : "s"}` : null}
                      {" "}need a rename decision before they can be added. Held-back legacy total: {formatCurrency(duplicateImportTotal)}.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {hasDuplicateConflicts && (
              <section className="space-y-4 rounded-xl border border-indigo-200 bg-indigo-50 dark:border-indigo-900/50 dark:bg-indigo-950/20 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                      <PencilLine className="h-4 w-4 text-indigo-600 dark:text-indigo-300" />
                      Duplicate codes need a decision
                    </div>
                    <p className="text-xs leading-5 text-slate-600 dark:text-slate-300 max-w-2xl">
                      These duplicate shot or asset rows were held back to protect unique codes in the app. Rename them here and we can add them into the project without losing the imported totals.
                    </p>
                  </div>
                  <div className="text-xs font-medium text-indigo-700 dark:text-indigo-200">
                    Held-back legacy total: {formatCurrency(duplicateImportTotal)}
                  </div>
                </div>

                {duplicateShots.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Duplicate Shots</h3>
                    <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                      <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 dark:bg-slate-900 text-slate-500 dark:text-slate-400">
                          <tr>
                            <th className="px-4 py-3 text-left font-medium">Original</th>
                            <th className="px-4 py-3 text-left font-medium">Occurrence</th>
                            <th className="px-4 py-3 text-left font-medium">Imported</th>
                            <th className="px-4 py-3 text-left font-medium">Rename To</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                          {duplicateShots.map((candidate) => (
                            <tr key={candidate.id} className="bg-white dark:bg-slate-950 align-top">
                              <td className="px-4 py-3">
                                <div className="font-medium text-slate-900 dark:text-slate-100">{candidate.originalCode}</div>
                                {candidate.description && (
                                  <div className="mt-1 text-xs text-slate-500 dark:text-slate-400 max-w-md">{candidate.description}</div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-slate-600 dark:text-slate-300">#{candidate.occurrence}</td>
                              <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{formatCurrency(candidate.imported)}</td>
                              <td className="px-4 py-3">
                                <input
                                  value={shotRenames[candidate.id] || ""}
                                  onChange={(event) => setShotRenames((current) => ({ ...current, [candidate.id]: event.target.value.toLowerCase() }))}
                                  className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {duplicateAssets.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Duplicate Assets</h3>
                    <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                      <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 dark:bg-slate-900 text-slate-500 dark:text-slate-400">
                          <tr>
                            <th className="px-4 py-3 text-left font-medium">Original</th>
                            <th className="px-4 py-3 text-left font-medium">Occurrence</th>
                            <th className="px-4 py-3 text-left font-medium">Imported</th>
                            <th className="px-4 py-3 text-left font-medium">Rename To</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                          {duplicateAssets.map((candidate) => (
                            <tr key={candidate.id} className="bg-white dark:bg-slate-950 align-top">
                              <td className="px-4 py-3">
                                <div className="font-medium text-slate-900 dark:text-slate-100">{candidate.originalCode}</div>
                                {candidate.description && (
                                  <div className="mt-1 text-xs text-slate-500 dark:text-slate-400 max-w-md">{candidate.description}</div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-slate-600 dark:text-slate-300">#{candidate.occurrence}</td>
                              <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{formatCurrency(candidate.imported)}</td>
                              <td className="px-4 py-3">
                                <input
                                  value={assetRenames[candidate.id] || ""}
                                  onChange={(event) => setAssetRenames((current) => ({ ...current, [candidate.id]: event.target.value }))}
                                  className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    onClick={handleApplyDuplicates}
                    className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700"
                  >
                    Apply Renamed Duplicates
                  </button>
                </div>
              </section>
            )}

            {hasAccuracySummary && (
              <div className="space-y-3 pt-2">
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  {metrics.map(({ label, metric }) => (
                    <div key={label} className={`rounded-xl border p-3 ${getAccuracyTone(metric?.accuracy)}`}>
                      <div className="text-[11px] font-medium uppercase tracking-[0.18em]">{label}</div>
                      <div className="mt-2 text-xl font-bold">
                        {metric?.accuracy === null || metric?.accuracy === undefined ? 'N/A' : `${metric.accuracy}%`}
                      </div>
                      <div className="mt-2 text-[11px] leading-5">
                        <div>Imported: {formatCurrency(metric?.imported)}</div>
                        <div>Calculated: {formatCurrency(metric?.calculated)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {onsetComparison && (
              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Onset Comparison</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-4">
                    <div className="text-xs uppercase tracking-[0.16em] text-slate-500">Legacy Import</div>
                    <div className="mt-2 text-sm text-slate-900 dark:text-slate-100">
                      <div>Days: <span className="font-semibold">{formatDays(onsetComparison.importedDays)}</span></div>
                      <div>Total: <span className="font-semibold">{formatCurrency(onsetComparison.importedTotal)}</span></div>
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-4">
                    <div className="text-xs uppercase tracking-[0.16em] text-slate-500">App Calculation</div>
                    <div className="mt-2 text-sm text-slate-900 dark:text-slate-100">
                      <div>Days: <span className="font-semibold">{formatDays(onsetComparison.calculatedDays)}</span></div>
                      <div>Total: <span className="font-semibold">{formatCurrency(onsetComparison.calculatedTotal)}</span></div>
                    </div>
                  </div>
                </div>
              </section>
            )}

            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Episode Comparison</h3>
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-900 text-slate-500 dark:text-slate-400">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium">Episode</th>
                      <th className="px-4 py-3 text-left font-medium">Shots</th>
                      <th className="px-4 py-3 text-left font-medium">Imported</th>
                      <th className="px-4 py-3 text-left font-medium">Calculated</th>
                      <th className="px-4 py-3 text-left font-medium">Variance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {episodeRows.map((row) => (
                      <tr key={row.episode} className="bg-white dark:bg-slate-950">
                        <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">{row.episode}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{row.shotCount}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{formatCurrency(row.imported)}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{formatCurrency(row.calculated)}</td>
                        <td className={`px-4 py-3 font-semibold ${varianceTone(row.variance)}`}>{formatCurrency(row.variance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Asset Comparison</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-4">
                  <div className="text-xs uppercase tracking-[0.16em] text-slate-500">Legacy Import</div>
                  <div className="mt-2 text-lg font-bold text-slate-900 dark:text-slate-100">{formatCurrency(result.comparison?.assets?.importedTotal)}</div>
                </div>
                <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-4">
                  <div className="text-xs uppercase tracking-[0.16em] text-slate-500">App Calculation</div>
                  <div className="mt-2 text-lg font-bold text-slate-900 dark:text-slate-100">{formatCurrency(result.comparison?.assets?.calculatedTotal)}</div>
                </div>
              </div>
              {assetRows.length > 0 && (
                <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-900 text-slate-500 dark:text-slate-400">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium">Asset</th>
                        <th className="px-4 py-3 text-left font-medium">Imported</th>
                        <th className="px-4 py-3 text-left font-medium">Calculated</th>
                        <th className="px-4 py-3 text-left font-medium">Variance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {assetRows.map((row) => (
                        <tr key={row.code} className="bg-white dark:bg-slate-950">
                          <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">{row.code}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{formatCurrency(row.imported)}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{formatCurrency(row.calculated)}</td>
                          <td className={`px-4 py-3 font-semibold ${varianceTone(row.variance)}`}>{formatCurrency(row.variance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {shotRows.length > 0 && (
              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Shot Comparison</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Shown because at least one episode total does not reconcile.
                </p>
                <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-900 text-slate-500 dark:text-slate-400">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium">Episode</th>
                        <th className="px-4 py-3 text-left font-medium">Shot</th>
                        <th className="px-4 py-3 text-left font-medium">Imported</th>
                        <th className="px-4 py-3 text-left font-medium">Calculated</th>
                        <th className="px-4 py-3 text-left font-medium">Variance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {shotRows.map((row) => (
                        <tr key={row.shotCode} className="bg-white dark:bg-slate-950">
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{row.episode}</td>
                          <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">{row.shotCode}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{formatCurrency(row.imported)}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{formatCurrency(row.calculated)}</td>
                          <td className={`px-4 py-3 font-semibold ${varianceTone(row.variance)}`}>{formatCurrency(row.variance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </div>

          <div className="p-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
            <button
              onClick={onClose}
              className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-colors"
            >
              Dismiss Report
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
